import { Comments } from "@/components/Comments";
import { PageContainer } from "@/components/layout/PageContainer";
import { PopularPosts } from "@/components/PopularPosts";
import { ReportButton } from "@/components/ReportButton";
import { TrustBadge, calculateTrustLevel } from "@/components/TrustBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { normalizeImageUrl } from "@/lib/image-url";
import { apiRequest, queryClient, unwrapPayload } from "@/lib/queryClient";
import { tryOpenKakaoShareSheet } from "@/lib/kakao-share";
import { formatRelativeTimeKo } from "@/lib/safe-date";
import {
  getCommunityContentIntent,
  getCommunityContentIntentLabel,
  type CommunityIntentRecord,
} from "@shared/community-content-intent";
import { communityTalkCategories, type CommunityTalk } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Clock,
  Eye,
  Heart,
  MapPin,
  MessageCircle,
  MoreVertical,
  Share2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";

export default function CommunityTalkDetail() {
  const [match, params] = useRoute<{ id: string }>("/community-talk/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const idParam = match && params ? params.id : "";

  // Fetch post detail
  const {
    data: post,
    isLoading,
    error: postError,
  } = useQuery<CommunityTalk & CommunityIntentRecord>({
    queryKey: [`/api/community-talk/${idParam}`],
    queryFn: async () => {
      if (!idParam) throw new Error("Invalid post ID");
      const response = await fetch(`/api/community-talk/${idParam}`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch post: ${response.status} ${errorText}`
        );
      }
      const payload = await response.json();
      return unwrapPayload<CommunityTalk & CommunityIntentRecord>(payload);
    },
    enabled: !!idParam,
  });

  // Track view count - only once per page load
  const [viewTracked, setViewTracked] = useState(false);

  // Increment view count on mount (only once)
  const viewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/community-talk/${idParam}/view`
      );
      if (!res.ok) {
        throw new Error("Failed to track view");
      }
      return res.json();
    },
    onSuccess: () => {
      setViewTracked(true);
      // Invalidate post query to refresh view count
      queryClient.invalidateQueries({
        queryKey: [`/api/community-talk/${idParam}`],
      });
    },
    onError: (error: any) => {
      // Silently fail - don't show error to user for view tracking
      console.error("[CommunityTalkDetail] View tracking error:", error);
    },
  });

  // Track view on mount - only once
  useEffect(() => {
    if (
      idParam &&
      !viewTracked &&
      !viewMutation.isPending &&
      !viewMutation.isSuccess
    ) {
      viewMutation.mutate();
    }
  }, [idParam]); // Only run when idParam changes

  // Like mutation
  const likeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/community-talk/${idParam}/like`
      );
      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/community-talk/${idParam}`],
      });
      toast({ description: data?.liked === false ? "좋아요를 취소했습니다" : "좋아요를 눌렀습니다" });
    },
    onError: (error: any) => {
      console.error("Like error:", error);
      toast({
        description: error?.message || "좋아요 처리 중 오류가 발생했습니다",
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/community-talk/${idParam}`),
    onSuccess: () => {
      toast({ description: "게시글이 삭제되었습니다" });
      navigate("/community-talk");
    },
  });

  // Keep route validation after hooks so route transitions preserve hook order.
  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">
              유효하지 않은 게시글 ID입니다
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/community-talk")}
            >
              동네 톡톡 목록
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">게시글을 찾을 수 없습니다</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/community-talk")}
            >
              동네 톡톡 목록
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const categoryInfo = communityTalkCategories.find(
    (c) => c.id === post.category
  );
  const contentIntent = getCommunityContentIntent(post);
  const isAuthor = user?.uid === post.authorId;

  return (
    <PageContainer size="read">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          onClick={() => navigate("/community-talk")}
          data-testid="button-back"
        >
          ← 동네 톡톡
        </Button>

        {isAuthor && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="button-menu">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => navigate(`/community-talk/edit/${idParam}`)}
                data-testid="button-edit"
              >
                수정하기
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => deleteMutation.mutate()}
                className="text-destructive"
                data-testid="button-delete"
              >
                삭제하기
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Main Card */}
      <Card className="p-6 mb-6">
        <div className="mb-4 rounded-lg bg-muted/60 p-3 text-sm leading-6 text-muted-foreground">
          {contentIntent === "question"
            ? "이 글은 사용자가 답을 구하는 생활 질문입니다. "
            : contentIntent === "experience"
              ? "이 글은 사용자가 직접 해결한 과정을 공유한 경험입니다. "
              : "이 글은 이웃과 나누는 동네 이야기입니다. "}
          공식 자료를 확인한 해결 안내는
          {" "}<button type="button" className="font-semibold text-primary hover:underline" onClick={() => navigate("/topics")}>생활문제 해결 가이드</button>에서 확인하세요.
        </div>
        {/* Content type and category badges */}
        <div className="mb-4 flex flex-wrap gap-2">
          <Badge>{getCommunityContentIntentLabel(contentIntent)}</Badge>
          {categoryInfo && <Badge variant="outline">{categoryInfo.label}</Badge>}
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold mb-4">{post.title}</h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6 pb-6 border-b">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              {post.authorNickname?.[0] || "익"}
            </div>
            <div className="flex items-center gap-2">
              <div className="font-medium text-foreground">
                {post.authorNickname || "익명"}
              </div>
              <TrustBadge
                level={calculateTrustLevel(
                  post.authorPostCount || 0,
                  post.authorSalesCount || 0
                )}
                postCount={post.authorPostCount || 0}
                salesCount={post.authorSalesCount || 0}
                size="sm"
                showLabel={true}
              />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {post.suburb}
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {formatRelativeTimeKo(post.createdAt)}
          </div>
          <div className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {post.views}
          </div>
        </div>

        {/* Content */}
        <div className="prose max-w-none mb-6">
          <p className="whitespace-pre-wrap">{post.content}</p>
        </div>

        {/* Images */}
        {post.imageUrls &&
          Array.isArray(post.imageUrls) &&
          post.imageUrls.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              {post.imageUrls.map((url, index) => {
                const normalizedUrl = normalizeImageUrl(url);
                return (
                  <img
                    key={index}
                    src={normalizedUrl || ""}
                    alt={`Image ${index + 1}`}
                    className="w-full rounded-lg object-cover"
                    onError={(e) => {
                      console.error(
                        `[CommunityTalkDetail] Image load error for URL: ${url}, normalized: ${normalizedUrl}`
                      );
                      e.currentTarget.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                    onLoad={() => {
                      console.log(
                        `[CommunityTalkDetail] Image loaded successfully: ${normalizedUrl}`
                      );
                    }}
                  />
                );
              })}
            </div>
          )}

        {/* Actions */}
        <div className="space-y-4 pt-6 border-t">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => likeMutation.mutate()}
              disabled={likeMutation.isPending}
              data-testid="button-like"
            >
              <Heart
                className={`h-4 w-4 ${
                  likeMutation.isSuccess ? "fill-current text-red-500" : ""
                }`}
              />
              {contentIntent === "question"
                ? "나도 궁금해요"
                : contentIntent === "experience"
                  ? "도움돼요"
                  : "좋아요"} {post.likes}
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              data-testid="button-comment"
              onClick={() => {
                // Scroll to comments section
                const commentsSection =
                  document.getElementById("comments-section");
                if (commentsSection) {
                  commentsSection.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            >
              <MessageCircle className="h-4 w-4" />
              {contentIntent === "question"
                ? "답변"
                : contentIntent === "experience"
                  ? "추가 질문·의견"
                  : "댓글"} {post.commentCount}
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              data-testid="button-share"
              onClick={async () => {
                // ③B "카카오톡으로 공유" (16장): try Kakao Talk's share sheet
                // first, fall back to the existing native share/clipboard flow.
                const opened = tryOpenKakaoShareSheet({
                  path: `/community-talk/${idParam || post.id}`,
                  contentType: "community_talk",
                  title: post.title,
                  description: post.content?.substring(0, 100) || undefined,
                  ctaLabel: "글 보러가기",
                });
                if (opened) {
                  return;
                }
                try {
                  const shareData = {
                    title: post.title,
                    text: post.content?.substring(0, 100) || post.title,
                    url: window.location.href,
                  };

                  if (navigator.share) {
                    await navigator.share(shareData);
                    toast({ description: "공유되었습니다" });
                  } else {
                    // Fallback: copy to clipboard
                    await navigator.clipboard.writeText(window.location.href);
                    toast({ description: "링크가 클립보드에 복사되었습니다" });
                  }
                } catch (error: any) {
                  if (error.name !== "AbortError") {
                    // User cancelled or error occurred
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      toast({
                        description: "링크가 클립보드에 복사되었습니다",
                      });
                    } catch (clipboardError) {
                      toast({
                        description: "공유에 실패했습니다",
                        variant: "destructive",
                      });
                    }
                  }
                }
              }}
            >
              <Share2 className="h-4 w-4" />
              공유
            </Button>
            {!isAuthor && (
              <ReportButton
                reportedType="community_talk"
                reportedId={idParam || post.id}
                reportedUserId={post.authorId}
                variant="outline"
              />
            )}
          </div>
        </div>
      </Card>

      {/* Comments Section */}
      <Card className="p-6" id="comments-section">
        <Comments
          articleId={idParam}
          targetType="community_talk"
          postAuthorId={post.authorId}
          isPostAuthor={isAuthor}
          responseMode={
            contentIntent === "question"
              ? "answer"
              : contentIntent === "experience"
                ? "followup"
                : "comment"
          }
        />
      </Card>

      {/* Popular Posts Section (Danggeun-style) */}
      <div className="mt-6">
        <PopularPosts
          suburb={post.suburb}
          excludeId={idParam}
          limit={5}
          hours={24}
          showHeader={true}
        />
      </div>
    </PageContainer>
  );
}
